import React, { useMemo } from "react";
import {
	Box,
	Button,
	CardContent,
	Checkbox,
	Chip,
	CircularProgress,
	Typography
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { StyledCard } from "./styled";

const HEADERS = [
	"Bar ID",
	"Name",
	"Type",
	"Address",
	"Description",
	"Phone",
	"Website",
	"Email",
	"Location (PostGIS)",
	"Cuisine",
	"Hours (JSONB)",
	"WiFi",
	"Outdoor Seating",
	"Categories",
	"Cover Image",
	"Collected At"
];

const CollectedDataCard = ({
	osmRawData,
	selectedRows,
	onSelectAll,
	onSelectRow,
	onRequestDelete,
	onRefresh,
	loadingRawData
}) => {
	const latestCollectionDate = useMemo(() => {
		if (!osmRawData || osmRawData.length === 0) {
			return "—";
		}

		const latestTimestamp = Math.max(
			...osmRawData.map((item) => {
				if (!item.firebase_created_at) return 0;
				if (item.firebase_created_at.seconds) {
					return item.firebase_created_at.seconds * 1000;
				}
				if (item.firebase_created_at instanceof Date) {
					return item.firebase_created_at.getTime();
				}
				if (typeof item.firebase_created_at === "number") {
					return item.firebase_created_at;
				}
				return 0;
			})
		);

		return latestTimestamp > 0 ? new Date(latestTimestamp).toLocaleString() : "—";
	}, [osmRawData]);

	const amenityTypes = useMemo(() => {
		if (!osmRawData || osmRawData.length === 0) {
			return {};
		}

		return osmRawData.reduce((acc, item) => {
			const amenity = item.osm_metadata?.amenity || "unknown";
			acc[amenity] = (acc[amenity] || 0) + 1;
			return acc;
		}, {});
	}, [osmRawData]);

	const renderSummary = () => (
		<Box>
			<Typography variant="body2" color="textSecondary" gutterBottom>
				<strong>Types:</strong>
			</Typography>
			<Box display="flex" flexWrap="wrap" gap={1}>
				{Object.entries(amenityTypes).map(([type, count]) => (
					<Chip key={type} label={`${type}: ${count}`} size="small" variant="outlined" color="primary" />
				))}
			</Box>
		</Box>
	);

	return (
		<StyledCard>
			<CardContent>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h6">Collected OSM Data ({osmRawData.length} records)</Typography>
					<Box display="flex" gap={1} alignItems="center">
						{selectedRows.size > 0 && (
							<Button
								variant="contained"
								color="error"
								size="small"
								startIcon={<Delete />}
								onClick={onRequestDelete}
							>
								Delete {selectedRows.size} Row{selectedRows.size !== 1 ? "s" : ""}
							</Button>
						)}
						<Button variant="outlined" size="small" onClick={onRefresh} disabled={loadingRawData}>
							Refresh
						</Button>
					</Box>
				</Box>

				{loadingRawData ? (
					<Box display="flex" alignItems="center" gap={2}>
						<CircularProgress size={20} />
						<Typography>Loading existing OSM data...</Typography>
					</Box>
				) : osmRawData.length > 0 ? (
					<>
						<Box mb={2}>
							<Typography variant="body2" color="textSecondary">
								Raw establishment data collected from OpenStreetMap. These records will be used for merging with existing bar data.
							</Typography>
							<Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
								<strong>Latest collection:</strong> {latestCollectionDate}
							</Typography>
						</Box>

						<Box mb={2}>{renderSummary()}</Box>

						<Box
							sx={{
								overflowX: "auto",
								border: "1px solid #e0e0e0",
								borderRadius: "8px",
								position: "relative"
							}}
						>
							<Box
								sx={{
									maxHeight: "600px",
									overflowY: "auto"
								}}
							>
								<Box
									sx={{
										display: "grid",
										gridTemplateColumns:
											"50px 200px 180px 120px 250px 200px 120px 150px 120px 150px 100px 150px 80px 120px 200px 100px 100px",
										gap: "1px",
										backgroundColor: "#f5f5f5",
										minWidth: "2250px"
									}}
								>
									<Box
										sx={{
											p: 2,
											backgroundColor: "white",
											fontWeight: "bold",
											borderBottom: "2px solid #e0e0e0",
											position: "sticky",
											top: 0,
											zIndex: 10,
											display: "flex",
											alignItems: "center",
											justifyContent: "center"
										}}
									>
										<Checkbox
											checked={selectedRows.size === osmRawData.length && osmRawData.length > 0}
											indeterminate={selectedRows.size > 0 && selectedRows.size < osmRawData.length}
											onChange={onSelectAll}
										/>
									</Box>
									{HEADERS.map((header) => (
										<Box
											key={header}
											sx={{
												p: 2,
												backgroundColor: "white",
												fontWeight: "bold",
												borderBottom: "2px solid #e0e0e0",
												position: "sticky",
												top: 0,
												zIndex: 10
											}}
										>
											{header}
										</Box>
									))}

									{osmRawData.map((establishment) => (
										<React.Fragment key={establishment.id}>
											<Box
												sx={{
													p: 2,
													backgroundColor: "white",
													borderBottom: "1px solid #e0e0e0",
													display: "flex",
													alignItems: "center",
													justifyContent: "center"
												}}
											>
												<Checkbox
													checked={selectedRows.has(establishment.id)}
													onChange={() => onSelectRow(establishment.id)}
												/>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.75rem", fontFamily: "monospace" }}>
													{establishment.bar_id}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontWeight: "medium" }}>
													{establishment.bar_name || "Unnamed"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Chip
													size="small"
													label={establishment.osm_metadata?.amenity || "Unknown"}
													color="primary"
													variant="outlined"
												/>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.bar_address || "No address"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.bar_description || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.bar_phone || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												{establishment.bar_url ? (
													<Typography
														variant="body2"
														sx={{
															fontSize: "0.75rem",
															color: "primary.main",
															cursor: "pointer",
															wordBreak: "break-all"
														}}
														component="a"
														href={establishment.bar_url}
														target="_blank"
														rel="noopener noreferrer"
													>
														{establishment.bar_url}
													</Typography>
												) : (
													<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>—</Typography>
												)}
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.bar_email || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography
													variant="body2"
													sx={{ fontSize: "0.75rem", fontFamily: "monospace", color: "text.secondary" }}
												>
													{establishment.location}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.osm_metadata?.cuisine || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.hours?.raw_opening_hours || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.osm_metadata?.wifi || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>
													{establishment.osm_metadata?.outdoor_seating || "—"}
												</Typography>
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												{renderCategories(establishment.bar_categories)}
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												{establishment.bar_cover_image ? (
													<Typography
														variant="body2"
														sx={{
															fontSize: "0.75rem",
															color: "primary.main",
															wordBreak: "break-all"
														}}
														component="a"
														href={establishment.bar_cover_image}
														target="_blank"
														rel="noopener noreferrer"
													>
														Image
													</Typography>
												) : (
													<Typography variant="body2" sx={{ fontSize: "0.85rem" }}>—</Typography>
												)}
											</Box>
											<Box sx={{ p: 2, backgroundColor: "white", borderBottom: "1px solid #e0e0e0" }}>
												<Typography variant="body2" sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
													{formatCollectedAt(establishment.firebase_created_at)}
												</Typography>
											</Box>
										</React.Fragment>
									))}
								</Box>
							</Box>
						</Box>
					</>
				) : (
					<Box textAlign="center" py={4}>
						<Typography variant="body1" color="textSecondary" gutterBottom>
							No OSM data collected yet
						</Typography>
						<Typography variant="body2" color="textSecondary">
							Run the data collection above to populate this section with establishments from OpenStreetMap. Once data is collected, you&apos;ll be able to review and merge it with existing bar records.
						</Typography>
					</Box>
				)}
			</CardContent>
		</StyledCard>
	);
};

const renderCategories = (categories) => {
	if (!categories) {
		return (
			<Typography variant="body2" sx={{ fontSize: "0.85rem", color: "text.secondary", fontStyle: "italic" }}>
				N/A (OSM)
			</Typography>
		);
	}

	const filteredEntries = Object.entries(categories).filter(
		([day]) => !["id", "bar_id", "created_at", "updated_at"].includes(day)
	);

	if (filteredEntries.length === 0) {
		return null;
	}

	return (
		<Box>
			{filteredEntries.map(([day, categoriesJson]) => {
				try {
					const parsed = typeof categoriesJson === "string" ? JSON.parse(categoriesJson) : categoriesJson;
					if (!parsed || Object.keys(parsed).length === 0) {
						return null;
					}

					const summary = Object.entries(parsed)
						.map(([type, items]) => `${type}: ${items.join(", ")}`)
						.join(" | ");

					if (!summary) {
						return null;
					}

					return (
						<Typography key={day} variant="caption" sx={{ display: "block", fontSize: "0.7rem", mb: 0.5 }}>
							<strong>{day.charAt(0).toUpperCase() + day.slice(1)}:</strong> {summary}
						</Typography>
					);
				} catch (error) {
					return null;
				}
			})}
		</Box>
	);
};

const formatCollectedAt = (value) => {
	if (!value) return "—";
	if (value.seconds) return new Date(value.seconds * 1000).toLocaleString();
	if (value instanceof Date) return value.toLocaleString();
	if (typeof value === "number") return new Date(value).toLocaleString();
	return "—";
};

export default CollectedDataCard;
